
/**
 * Diagnostics Engine for MA Syntax Validation
 */
export class DiagnosticsEngine {
    constructor(keywordsData, version) {
        this.keywordsData = keywordsData;
        this.version = version;
        this.operators = ["thru", "at", "if", "except", "and", "on", "off", "with", "where"];
    }

    /**
     * Validates a line of code and returns Monaco marker data
     */
    validateLine(line, lineNumber) {
        if (!line || line.trim() === "") return [];

        let workingLine = line.trim();
        let conditionOffset = 0;

        // 1. Handle MA2 Conditional blocks [var==val]
        if (workingLine.startsWith('[')) {
            const closingBracket = workingLine.indexOf(']');
            if (closingBracket !== -1) {
                // For now, we just skip validating inside the condition 
                // but preserve the offset for correct marker positioning
                conditionOffset = closingBracket + 1;
                workingLine = workingLine.substring(conditionOffset).trim();
                conditionOffset = line.indexOf(workingLine); // Recalculate true start of command
            }
        }

        const markers = [];
        const tokens = this.tokenizeWithPositions(workingLine);
        if (tokens.length === 0) return [];

        // Adjust token positions to match original line
        tokens.forEach(t => {
            t.start += conditionOffset;
            t.end += conditionOffset;
        });

        const firstToken = tokens[0];
        const lowerFirst = firstToken.text.toLowerCase();

        // Find keyword in data case-insensitively
        const kwName = Object.keys(this.keywordsData).find(k => k.toLowerCase() === lowerFirst);
        const keywordData = kwName ? this.keywordsData[kwName] : null;

        if (!keywordData) {
            // Check if it's a common operator but out of place
            if (this.operators.includes(firstToken.text.toLowerCase())) {
                markers.push({
                    severity: 4, // Error
                    message: `Operator '${firstToken.text}' cannot be used at the start of a command.`,
                    startLineNumber: lineNumber,
                    startColumn: firstToken.start + 1,
                    endLineNumber: lineNumber,
                    endColumn: firstToken.end + 1
                });
            } else if (!this.isNumber(firstToken.text) && !firstToken.text.startsWith('"')) {
                // Unknown command
                markers.push({
                    severity: 4,
                    message: `Unknown command or keyword: '${firstToken.text}'`,
                    startLineNumber: lineNumber,
                    startColumn: firstToken.start + 1,
                    endLineNumber: lineNumber,
                    endColumn: firstToken.end + 1
                });
            }
            return markers; // Stop if first word is unknown
        }

        // 2. Structural validation against syntax models
        const models = keywordData.syntax_models || [];
        if (models.length === 0) return markers;

        // Try to find a matching model
        let bestMatch = null;
        let maxTokensMatched = -1;

        for (const model of models) {
            const matchResult = this.checkAgainstModel(tokens, model);
            if (matchResult.isValid) return []; // Full match found

            if (matchResult.tokensMatched > maxTokensMatched) {
                maxTokensMatched = matchResult.tokensMatched;
                bestMatch = matchResult;
            }
        }

        // If no full match, report specific error from best match or general error
        if (bestMatch && bestMatch.error) {
            markers.push({
                severity: 4,
                message: bestMatch.error.message,
                startLineNumber: lineNumber,
                startColumn: bestMatch.error.token.start + 1,
                endLineNumber: lineNumber,
                endColumn: bestMatch.error.token.end + 1
            });
        }

        return markers;
    }

    /**
     * Check tokens against a specific syntax model
     */
    checkAgainstModel(tokens, model) {
        // Simple model check: 
        // Iterate through model tokens and see if input tokens match types/values
        let tokenIdx = 0;
        let modelIdx = 0;

        while (tokenIdx < tokens.length && modelIdx < model.length) {
            const token = tokens[tokenIdx];
            const mToken = model[modelIdx];

            if (this.tokensMatch(token, mToken)) {
                tokenIdx++;
                modelIdx++;
            } else if (!mToken.required) {
                // If current model token is optional, skip it and check next model token
                modelIdx++;
            } else {
                // Mismatch on a required token
                return {
                    isValid: false,
                    tokensMatched: tokenIdx,
                    error: {
                        message: `Expected ${mToken.type} '${mToken.label || mToken.value}', but found '${token.text}'`,
                        token: token
                    }
                };
            }
        }

        // Check if all required model tokens were covered
        while (modelIdx < model.length) {
            if (model[modelIdx].required) {
                return {
                    isValid: false,
                    tokensMatched: tokenIdx,
                    error: {
                        message: `Missing required ${model[modelIdx].type}: ${model[modelIdx].label || model[modelIdx].value}`,
                        token: tokens[tokens.length - 1]
                    }
                };
            }
            modelIdx++;
        }

        // If we have trailing tokens that weren't matched
        if (tokenIdx < tokens.length) {
            return {
                isValid: false,
                tokensMatched: tokenIdx,
                error: {
                    message: `Unexpected token '${tokens[tokenIdx].text}'`,
                    token: tokens[tokenIdx]
                }
            };
        }

        return { isValid: true, tokensMatched: tokenIdx };
    }

    tokenizeWithPositions(str) {
        const tokens = [];
        let current = "";
        let start = 0;
        let inQuotes = false;
        let inParentheses = false;

        for (let i = 0; i < str.length; i++) {
            const char = str[i];

            if (char === '"') {
                inQuotes = !inQuotes;
                current += char;
            } else if (char === '(' && !inQuotes) {
                inParentheses = true;
                current += char;
            } else if (char === ')' && !inQuotes) {
                inParentheses = false;
                current += char;
            } else if (char === ' ' && !inQuotes && !inParentheses) {
                if (current) {
                    tokens.push({ text: current, start: start, end: i });
                }
                current = "";
                start = i + 1;
            } else {
                current += char;
            }
        }
        if (current) tokens.push({ text: current, start: start, end: str.length });
        return tokens;
    }

    isNumber(str) {
        if (!str) return false;
        // Handle numbers, ranges, and MA object keys like 1.2.3
        return /^[0-9.+-]+$/.test(str);
    }

    isKeywordWithType(name, type) {
        if (!name) return false;
        const lowerName = name.toLowerCase();
        const kw = Object.keys(this.keywordsData).find(k => k.toLowerCase() === lowerName);
        const data = kw ? this.keywordsData[kw] : null;
        return data && data.semantic_type === type;
    }

    tokensMatch(token, mToken) {
        const text = token.text;
        const lowerText = text.toLowerCase();

        // 0. Dynamic Variables ($var) match ALMOST anything that isn't a strict keyword
        if (text.startsWith('$')) return true;

        // 1. Literal match (Value or Label)
        if (mToken.value && lowerText === mToken.value.toLowerCase()) return true;

        // 2. Type-based match
        switch (mToken.type) {
            case 'number': return this.isNumber(text);
            case 'object':
                return this.isKeywordWithType(text, 'object') || this.isNumber(text);
            case 'keyword':
                const kName = Object.keys(this.keywordsData).find(k => k.toLowerCase() === lowerText);
                return !!kName || this.operators.includes(lowerText);
            case 'option': return text.startsWith('/');
            case 'value': return true;
            case 'string': return text.startsWith('"') && text.endsWith('"');
            case 'operator': return this.operators.includes(lowerText) || lowerText === '+' || lowerText === '-' || lowerText === 'thru';
            case 'identifier': return true;
        }

        return false;
    }
}
